import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchQuote } from "../services/quoteService";
import type {
  AppliedCoupon,
  BillingCycleId,
  PriceBreakdown,
} from "../types/billing.types";

export type QuoteStatus = "loading" | "ready" | "failed";

export interface QuoteState {
  /** What the order comes to, or `null` until the first quote lands. */
  price: PriceBreakdown | null;
  /** The coupon the server allowed, which is the only coupon there is. */
  coupon: AppliedCoupon | null;
  status: QuoteStatus;
  error?: string;
  /** A re-quote is with the server. */
  pricing: boolean;
  /**
   * Prices the plan again.
   *
   * It takes no code, because there is nowhere to send one — see `fetchQuote`.
   * This is what the checkout's Apply and Remove both do: ask the server what
   * the order comes to, and report the coupon it came back with, which is
   * `null` unless the server applied one itself.
   *
   * Throws where the quote was refused, carrying the reason. Either way the
   * figures on screen are only ever replaced by figures that came back.
   */
  reprice: () => Promise<AppliedCoupon | null>;
  reload: () => void;
}

/**
 * What this plan costs today, priced by the backend.
 *
 * Asked as the checkout opens, and again whenever the checkout's coupon row is
 * pressed — which is the same call, since no code travels. The coupon in force
 * lives here rather than in the screen for that reason: what is on the order is
 * whatever the last quote came back carrying, and nothing the page believes
 * about a code survives being contradicted by it.
 *
 * Waits for a plan. The price list is itself a fetch, so there is a moment with
 * no code to quote — see `usePlans` — and asking for a quote on `undefined`
 * would be a 400 for a mistake nobody made.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useQuote(planCode?: string, cycle?: BillingCycleId): QuoteState {
  const getToken = useTokenGetter();

  const [price, setPrice] = useState<PriceBreakdown | null>(null);
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  const [status, setStatus] = useState<QuoteStatus>("loading");
  const [error, setError] = useState<string | undefined>(undefined);
  const [pricing, setPricing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // No plan yet. Still `loading` rather than `failed`: nothing has been asked
    // and nothing has been refused, and the screen is already waiting on the
    // price list this will follow.
    if (!planCode || !cycle) return;

    const controller = new AbortController();
    setStatus("loading");

    fetchQuote(getToken, { planCode, cycle }, controller.signal)
      .then((quoted) => {
        if (controller.signal.aborted) return;
        setPrice(quoted.price);
        setCoupon(quoted.coupon);
        setError(undefined);
        setStatus("ready");
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setStatus("failed");
        setError(
          cause instanceof ApiError
            ? cause.message
            : "We could not reach the server. Check your connection and try again."
        );
        console.error("quote failed to load:", cause);
      });

    return () => controller.abort();
  }, [getToken, planCode, cycle, attempt]);

  const reload = useCallback(() => setAttempt((current) => current + 1), []);

  const reprice = useCallback(async () => {
    if (!planCode || !cycle) {
      // A bug at the call site: the coupon row is not drawn until there is a
      // plan to price.
      throw new Error("No plan to price.");
    }

    setPricing(true);
    try {
      const quoted = await fetchQuote(getToken, { planCode, cycle });

      setPrice(quoted.price);
      setCoupon(quoted.coupon);
      setStatus("ready");
      setError(undefined);
      return quoted.coupon;
    } finally {
      setPricing(false);
    }
  }, [getToken, planCode, cycle]);

  return { price, coupon, status, error, pricing, reprice, reload };
}

export default useQuote;
